import { Avatar } from '@mui/material';
import { Badge, Button, Table } from 'flowbite-react';
import moment from 'moment';
import React, { useCallback, useEffect, useState } from 'react';
import { generatePositionTitle, stringAvatar } from 'shared-utils';
import useDisclosure from 'src/hooks/utils/useDisclosure';
import { generateCompanyLogoAvatarSrc } from 'src/utils/companyLogo';
import { BriefDebriefCallDocument } from 'types';
import BriefDebriefModalV2 from '../recruiterPositions/BriefDebriefModalV2';

interface Props {
	calls: BriefDebriefCallDocument[];
}

const AMBriefDebriefContainer = ({ calls }: Props) => {
	const { isOpen, onOpen, onClose } = useDisclosure();

	const [avatarImg, setAvatarImg] = useState('');
	const [selectedCall, setSelectedCall] = useState<BriefDebriefCallDocument>();
	const company = calls[0].position?.company;

	const generateAvatar = useCallback(async () => {
		const res = await generateCompanyLogoAvatarSrc(company?.logo || '', company?.url);
		setAvatarImg(res);
	}, [company?.logo, company?.url]);

	useEffect(() => {
		generateAvatar();
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [company?.logo, company?.url]);

	return (
		<>
			<div className="grid my-5 overflow-auto border rounded-[0.6rem] whitespace-pre drop-shadow-md">
				<Table>
					<Table.Head>
						<Table.HeadCell>Company & Position</Table.HeadCell>
						<Table.HeadCell>Round & Type</Table.HeadCell>
						<Table.HeadCell>Date & Time</Table.HeadCell>
						<Table.HeadCell>Assigned To</Table.HeadCell>
						<Table.HeadCell>Status</Table.HeadCell>
						<Table.HeadCell>Actions</Table.HeadCell>
					</Table.Head>

					{calls.map(call => (
						<Table.Row key={call?._id} className="text-black border-b w-full ">
							<Table.Cell>
								<div className="flex gap-2 items-center">
									<div className="flex justify-center items-center text-center min-w-[32px] min-h-[32px] max-w-[32px] max-h-[32px] rounded-full border border-[#F3F2F2]">
										<Avatar
											alt="companyLogo"
											src={avatarImg}
											sx={{ width: '28px', height: '28px', minWidth: '28px', minHeight: '28px', fontSize: '12px' }}
										>
											{stringAvatar(company?.name.trim().toUpperCase() || '')}
										</Avatar>
									</div>
									<div className="flex flex-col">
										<div className="font-medium">{call.position?.company?.name}</div>
										<div className="font-normal text-[12px]">{generatePositionTitle(call.position?.title)}</div>
										<div className="font-light text-[12px]">
											{call.users?.length || 0} {`Candidate${call.users.length > 1 ? 's' : ''}`}
										</div>
									</div>
								</div>
							</Table.Cell>
							<Table.Cell>
								<div className="flex flex-col">
									<div className="text-[14px] font-medium">
										{call.callType} - Round {call.roundNumber}
									</div>
									<div className="text-[12px] font-normal">{call.roundType}</div>
								</div>
							</Table.Cell>
							<Table.Cell>
								{call.meetingDateTime ? (
									<div className="flex flex-col ">
										<div>{moment(call.meetingDateTime).format('hh:mm a')}</div>
										<div>{moment(call.meetingDateTime).format('DD MMM, YYYY')}</div>
									</div>
								) : (
									'-'
								)}
							</Table.Cell>
							<Table.Cell>
								<div className="flex flex-col">
									{call.prepCoach ? (
										<>
											<div>{call.prepCoach.name}</div>
											<div>{call.prepCoach.email}</div>
										</>
									) : (
										call.additionalEmails?.map(email => <div>{email}</div>)
									)}
								</div>
							</Table.Cell>
							<Table.Cell>
								{call.meetingDateTime ? (
									<Badge className="flex items-center justify-center" color={'failure'}>
										Scheduled
									</Badge>
								) : (
									<Badge className="flex items-center justify-center">Requested</Badge>
								)}
							</Table.Cell>
							<Table.Cell>
								<Button
									color="purplePlaced"
									className="btn btn-sm btn-primary"
									onClick={() => {
										setSelectedCall(call);
										onOpen();
									}}
								>
									{call.meetingDateTime ? 'Reschedule' : 'Schedule'}
								</Button>
							</Table.Cell>
						</Table.Row>
					))}
				</Table>
			</div>

			<BriefDebriefModalV2
				callDetails={selectedCall}
				userIds={selectedCall?.users?.map(userId => String(userId))}
				rescheduleCallId={selectedCall?._id}
				selectedPosition={selectedCall?.position?._id}
				scheduleType="schedule"
				isOpen={isOpen}
				onClose={onClose}
			/>
		</>
	);
};

export default AMBriefDebriefContainer;
